/**
 * Hook decisioni prestito fine stagione.
 *
 * - useDecisioniPrestitoPendenti: lista decisioni da risolvere (filtrate
 *   server-side per ruolo: admin vede tutto, presidente solo i propri DDR)
 * - useRisolviDDR: il presidente sceglie riscatta/restituisci
 * - useVerificaODR: l'admin verifica la condizione di un ODR condizionato
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useAuthStore } from "../lib/auth-store";

export type EsitoPrestito = "riscattato" | "restituito";
export type TipoPossessoPrestito =
  | "prestito_secco"
  | "prestito_ddr"
  | "prestito_odr"
  | "prestito_odr_condizionato";
export type TipoDecisionePrestito =
  | "scelta_ddr"
  | "verifica_odr_condizionato";

export interface DecisionePrestito {
  id: string;
  stagione_id: string;
  giocatore_id: string;
  giocatore_nome: string;
  squadra_prestito_id: string;
  squadra_prestito_nome: string;
  squadra_proprietaria_id: string | null;
  squadra_proprietaria_nome: string | null;
  tipo_possesso: TipoPossessoPrestito;
  tipo_decisione: TipoDecisionePrestito;
  importo_riscatto_milioni: string | null;
  condizione_riscatto_testo: string | null;
  stato: "pendente" | "risolta";
  esito: EsitoPrestito | null;
  risolta_at: string | null;
}

export function useDecisioniPrestitoPendenti() {
  const isAuth = !!useAuthStore((s) => s.accessToken);
  return useQuery({
    queryKey: ["prestiti", "decisioni-pendenti"],
    queryFn: async () =>
      (await api.get<DecisionePrestito[]>("/prestiti/decisioni-pendenti")).data,
    enabled: isAuth,
  });
}

export function useRisolviDDR() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { decisioneId: string; esito: EsitoPrestito }) => {
      const res = await api.post<DecisionePrestito>(
        `/prestiti/decisioni/${args.decisioneId}/risolvi-ddr`,
        { esito: args.esito },
      );
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["prestiti"] });
      qc.invalidateQueries({ queryKey: ["rosa"] });
      qc.invalidateQueries({ queryKey: ["budget"] });
      qc.invalidateQueries({ queryKey: ["notifiche"] });
    },
  });
}

export function useVerificaODR() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { decisioneId: string; verificata: boolean }) => {
      const res = await api.post<DecisionePrestito>(
        `/prestiti/decisioni/${args.decisioneId}/verifica-odr`,
        { verificata: args.verificata },
      );
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["prestiti"] });
      qc.invalidateQueries({ queryKey: ["rosa"] });
      qc.invalidateQueries({ queryKey: ["budget"] });
      qc.invalidateQueries({ queryKey: ["notifiche"] });
    },
  });
}
